import {
  BUNDLE_PRODUCT_TYPE,
  GROUP_PRODUCT_REFERENCE,
  INVALID_DATE,
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_IN_PROGRESS,
} from 'features/order-tracker/services/constants';
import { formatDate } from 'features/order-tracker/utils/dateUtils';
import {
  APPOINTMENT_STATUSES,
  APPOINTMENT_TYPES,
  MILESTONE_STATUSES,
  ORDER_SERVICES_TYPES,
  PRODUCT_NAMES,
  PRODUCT_TYPES,
} from 'features/order-tracker/utils/entries';

import { getAppointmentInfo } from './helpers';
import { APPOINTMENTS_STATUSES, MILESTONES_NAMES_MAPPER } from './helpers/constants';

export class OrderSummaryService {
  private readonly responses: transformedResponse.OrderSummary[];

  constructor(responses: transformedResponse.OrderSummary[]) {
    this.responses = responses;
  }

  getSubOrdersStatuses = (): string[] =>
    this.responses.map((order: transformedResponse.OrderSummary) => order.OrderStatus);

  getSortedProducts = (): transformedResponse.Product[] => {
    const allProducts = this.getResponsesData('products');
    return allProducts
      .filter((product) => product.productType === BUNDLE_PRODUCT_TYPE)
      .concat(
        ...allProducts
          .filter((product) => product.productType !== BUNDLE_PRODUCT_TYPE)
          .sort((a, b) => (a.productType < b.productType ? 1 : -1)),
      );
  };

  mapServiceProduct = (product: dto.Product): transformedResponse.mapServiceProduct => {
    const { InstallationAddress, ProductName, EngineeringAppointmentDate, AppointmentStatus } = product;

    return {
      installationAddress: InstallationAddress,
      productName: ProductName,
      engineeringAppointmentDate: EngineeringAppointmentDate,
      appointmentStatus: AppointmentStatus,
    };
  };

  checkProduct = (product: dto.Product, name: string, type: number): boolean =>
    product.ProductName === name && product.ProductType === type && !product.isCeaseOrCancelled;

  getProductsInformation = (): transformedResponse.ProductsInformation => {
    const isHALO = this.responses[0]?.groupId?.startsWith(GROUP_PRODUCT_REFERENCE) ?? false;
    const isAppAndPhoneCVE =
      this.responses[0]?.allProducts.some((product) => product.ProductType === 4) ||
      this.responses[1]?.allProducts.some((product) => product.ProductType === 4);
    const isAppOnlyCVE =
      this.responses[0]?.allProducts.some((product) => product.ProductType === 11) ||
      this.responses[1]?.allProducts.some((product) => product.ProductType === 11);
    return this.responses.reduce(
      (acc, orderData) => {
        const { OrderStatus } = orderData;
        orderData.allProducts.forEach((product: dto.Product) => {
          const { EngineeringAppointmentDate, AppointmentStatus, FTTPInstallProcess } = product;

          if (EngineeringAppointmentDate && APPOINTMENTS_STATUSES.includes(AppointmentStatus)) {
            const { address, engineerAppointment, engineerAppointmentMilestone, isCompleted } = getAppointmentInfo({
              ...product,
              isShowAppointment: orderData.IsShowAppointment,
            });

            const updatedEngineerAppointments = engineerAppointment
              ? [...acc.engineerAppointments, engineerAppointment]
              : acc.engineerAppointments;

            updatedEngineerAppointments.sort(
              (a, b) => Date.parse(a.engineeringAppointmentDate) - Date.parse(b.engineeringAppointmentDate),
            );

            const updatedEngineerAppointmentsMilestones = engineerAppointmentMilestone
              ? [...acc.engineerAppointmentsMilestones, engineerAppointmentMilestone]
              : acc.engineerAppointmentsMilestones;

            acc.installationAddress = acc.installationAddress || address;
            acc.engineerAppointments = updatedEngineerAppointments;
            acc.engineerAppointmentsMilestones = updatedEngineerAppointmentsMilestones;
            acc.engineerAppointmentsCount += 1;
            acc.completedEngineerAppointmentsCount += +isCompleted;
          }

          this.checkProduct(product, PRODUCT_NAMES.BBProduct, PRODUCT_TYPES.BBProduct) &&
            acc.BBProducts.push({
              ...this.mapServiceProduct(product),
              isHALO,
              estimatedCompletionDate: orderData.EstimatedCompletionDate,
              isShowAppointment: orderData.IsShowAppointment,
              FTTPInstallProcess,
              OrderStatus:OrderStatus??null,
            });

          this.checkProduct(product, PRODUCT_NAMES.CVEProduct, PRODUCT_TYPES.CVEProduct) &&
            acc.CVEProducts.push({
              ...this.mapServiceProduct(product),
              isHALO,
              estimatedCompletionDate: orderData.EstimatedCompletionDate,
              isAppOnlyCVE,
              isAppAndPhoneCVE,
              FTTPInstallProcess,
              OrderStatus:OrderStatus??null,
            });
        });
        return acc;
      },
      {
        BBProducts: [] as transformedResponse.ServiceProduct[],
        CVEProducts: [] as transformedResponse.ServiceProduct[],
        installationAddress: '',
        engineerAppointments: [] as common.EngineerAppointment[],
        engineerAppointmentsMilestones: [] as common.OrderMilestone[],
        engineerAppointmentsCount: 0,
        completedEngineerAppointmentsCount: 0,
      },
    );
  };

  getResponsesData = (dataType: string): transformedResponse.Product[] =>
    this.responses.reduce((data: transformedResponse.Product[], orderData) => {
      const mappedData =
        orderData[dataType]?.map((item) => ({
          ...item,
          IsShowAppointment: orderData.IsShowAppointment,
          estimatedCompletionDate: orderData.EstimatedCompletionDate,
        })) || [];

      return [...data, ...mappedData];
    }, []);

  getOrderSummaryStatus = (): string => {
    const { OrderStatus } = this.responses[0];
    const isSingleOrderSummary = this.responses.length === 1;
    if (isSingleOrderSummary && OrderStatus !== ORDER_STATUS_COMPLETED) {
      return OrderStatus;
    }
    const latestDate = new Date(
      Math.max(...this.responses.map(({ EstimatedCompletionDate }) => Number(new Date(EstimatedCompletionDate)))),
    );
    if (INVALID_DATE === latestDate.toString()) {
      return ORDER_STATUS_COMPLETED;
    }

    return Date.now() >= latestDate.getTime() ? ORDER_STATUS_COMPLETED : ORDER_STATUS_IN_PROGRESS;
  };

  getOrderDescription = (products: transformedResponse.Product[]): string => {
    if (!products.length) {
      return '';
    }
    if (products[0].productType === 8) {
      return `${products[0].productName}:${products.length - 1}-non-bundle`;
    }
    return products.length === 1
      ? `${products[0].productName}:${products.length}-non-bundle`
      : `MultipleProd:${products.length}-non-bundle`;
  };

  getOrderPlacedMilestone = (placedOnDate: string): common.OrderMilestone => ({
    title: 'Order placed',
    displayTitle: MILESTONES_NAMES_MAPPER['Order placed'] || 'Order placed',
    id: 'orderPlaced',
    milestoneStatus: MILESTONE_STATUSES.complete,
    subTitle: formatDate('dddd D MMMM YYYY', placedOnDate || this.responses[0].placedOn),
  });

  getBillingMilestone = (status: string): common.OrderMilestone => ({
    title: 'New service ready to use',
    type: ORDER_SERVICES_TYPES.Billing,
    displayTitle: MILESTONES_NAMES_MAPPER['New service ready to use'] || 'New service ready to use',
    id: 'billing',
    milestoneStatus: status === ORDER_STATUS_COMPLETED ? MILESTONE_STATUSES.complete : MILESTONE_STATUSES.incomplete,
  });

  getHighDemandEngineerVisitsBanner = (engineerAppointments: common.EngineerAppointment[]): boolean =>
    engineerAppointments.some(
      ({ status, isKci2AssureOrder, isDateLessThan24Hours, type }) =>
        !isKci2AssureOrder &&
        !isDateLessThan24Hours &&
        type !== APPOINTMENT_TYPES.expert &&
        (status === APPOINTMENT_STATUSES.Booked || status === APPOINTMENT_STATUSES.Confirmed),
    );
}
